import type { TFunction } from "i18next";

import type {
  ConfigurationScopeId,
  ConfigurationScopeSummary,
} from "@thalamos/haven-common";

/** A configuration placed in its chain. */
export type ChainEntry = ConfigurationScopeSummary & {
  /** 1 for the configuration at the top of its chain, 2 for one under it. */
  level: number;
};

/** One chain: its top configuration and everything beneath it. */
export type Chain = {
  top: ChainEntry;
  /** Top first, each configuration followed by those that sit under it. */
  entries: readonly ChainEntry[];
  levels: number;
  organisationCount: number;
};

/**
 * The configurations grouped into the separate chains they form.
 *
 * Built here from the flat list the server returns, rather than being asked for
 * as a tree: a configuration's parent may be anywhere in the list, so a paged or
 * nested response would make the shape depend on how it was fetched.
 *
 * A configuration whose parent is missing — which nothing should produce —
 * starts a chain of its own rather than being dropped, so an inconsistency shows
 * up as something odd on the page rather than as a configuration that has
 * silently vanished.
 */
export const asChains = (
  scopes: readonly ConfigurationScopeSummary[],
): Chain[] => {
  const byParent = new Map<
    ConfigurationScopeId | null,
    ConfigurationScopeSummary[]
  >();

  for (const scope of scopes) {
    const siblings = byParent.get(scope.parentScopeId) ?? [];
    siblings.push(scope);
    byParent.set(scope.parentScopeId, siblings);
  }

  const known = new Set(scopes.map((scope) => scope.id));

  const walk = (
    scope: ConfigurationScopeSummary,
    level: number,
  ): ChainEntry[] => [
    { ...scope, level },
    ...(byParent.get(scope.id) ?? []).flatMap((child) =>
      walk(child, level + 1),
    ),
  ];

  return scopes
    .filter(
      (scope) =>
        scope.parentScopeId === null || !known.has(scope.parentScopeId),
    )
    .map((top) => {
      const entries = walk(top, 1);

      return {
        top: { ...top, level: 1 },
        entries,
        levels: Math.max(...entries.map((entry) => entry.level)),
        organisationCount: entries.reduce(
          (sum, entry) => sum + entry.organisationCount,
          0,
        ),
      };
    });
};

/**
 * Where one configuration sits: its chain, its own entry, and the
 * configurations above it, top first.
 */
export const findInChains = ({
  chains,
  scopeId,
}: {
  chains: readonly Chain[];
  scopeId: string;
}):
  | { chain: Chain; entry: ChainEntry; above: readonly ChainEntry[] }
  | undefined => {
  for (const chain of chains) {
    const entry = chain.entries.find((candidate) => candidate.id === scopeId);

    if (entry === undefined) {
      continue;
    }

    const byId = new Map(chain.entries.map((each) => [each.id, each]));
    const above: ChainEntry[] = [];
    let parentId = entry.parentScopeId;

    // Bounded in case `asChains` ever admits a cycle.
    while (parentId !== null && above.length < chain.entries.length) {
      const parent = byId.get(parentId);

      if (parent === undefined) {
        break;
      }

      above.unshift(parent);
      parentId = parent.parentScopeId;
    }

    return { chain, entry, above };
  }

  return undefined;
};

/** The configurations beneath one in its chain, at any depth. */
export const entriesBelow = ({
  chain,
  scopeId,
}: {
  chain: Chain;
  scopeId: ConfigurationScopeId;
}): ChainEntry[] => {
  const below = new Set([scopeId]);

  // Entries come parent first, so one pass sees each parent before its
  // children.
  return chain.entries.filter((entry) => {
    if (entry.parentScopeId !== null && below.has(entry.parentScopeId)) {
      below.add(entry.id);
      return true;
    }

    return false;
  });
};

/**
 * Every configuration, in chain order, labelled with its level and chain, as a
 * picker offers them to choose from.
 */
export const chainChoices = ({
  chains,
  t,
}: {
  chains: readonly Chain[];
  t: TFunction;
}): { id: ConfigurationScopeId; label: string }[] =>
  chains.flatMap((chain) =>
    chain.entries.map((entry) => ({
      id: entry.id,
      label:
        entry.level === 1
          ? t("admin.configuration.scope.parent.top", { name: entry.name })
          : t("admin.configuration.scope.parent.below", {
              name: entry.name,
              level: entry.level,
              chain: chain.top.name,
            }),
    })),
  );
